import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslate } from '@tolgee/react';
import {
  Button,
  Dropdown,
  Form,
  InputGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
  faArrowDownToLine,
  faArrowLeft,
  faBold,
  faBringForward,
  faEye,
  faEyeSlash,
  faItalic,
  faLock,
  faLockOpen,
  faPlus,
  faSendBackward,
  faTrash
} from '@fortawesome/pro-duotone-svg-icons';
import '@fortawesome/fontawesome-svg-core/styles.css';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import toast from 'react-hot-toast';
import { PinturaEditor } from '@pqina/react-pintura';
import {
  createDefaultImageReader,
  createDefaultImageWriter,
  getEditorDefaults,
  locale_en_gb,
  plugin_annotate,
  setPlugins,
  type ImageSource,
  type PinturaEditorOptions,
  type Shape
} from '@pqina/pintura';
import {
  LocaleAnnotate,
  LocaleCore,
  LocaleMarkupEditor
} from '@pqina/pintura/locale/en_GB';
import {
  LocaleAnnotate as LocaleAnnotateDe,
  LocaleCore as LocaleCoreDe,
  LocaleMarkupEditor as LocaleMarkupEditorDe
} from '@pqina/pintura/locale/de_DE';
import '@pqina/pintura/pintura.css';
import './LomavisTemplateEditor.css';
import { LomavisConfirmationModal } from '../../../molecules/feedback/LomavisConfirmationModal';
import {
  LomavisFontTypeahead,
  type LomavisFontOption
} from '../../../atoms/inputs/LomavisFontTypeahead';
import { LomavisSketchColorPicker } from '../../../atoms/inputs/LomavisSketchColorPicker';
import { LomavisDropdown1 } from '../../../atoms/overlays/LomavisDropdown1';
import { LomavisOverlayToolTip1 } from '../../../atoms/overlays/lomavisOverlayToolTip1/lomavisOverlayToolTip1';
import { LomavisImageUpload1 } from '../../../molecules/inputs/LomavisImageUpload1';
import {
  canvasPresets,
  createLayer,
  hasBackgroundLayer,
  resizeTemplateCanvas,
  serializeTemplate,
  TEMPLATE_EDITOR_THUMBNAIL_WIDTH,
  toPinturaShape,
  pinturaShapeToLayerPatch,
  type TemplateDefinition,
  type TemplateLayer,
  type TemplateLayerKind,
  type TemplatePinturaShape
} from './templateDefinition';

setPlugins(plugin_annotate);

type FontAwesomeIconProp = React.ComponentProps<
  typeof FontAwesomeIcon
>['icon'];

/* The Pro package resolves its Font Awesome common-types dependency separately
   from the React renderer. Its runtime icon objects are compatible, but their
   duplicated TypeScript declarations are not. Keep the cast at this boundary. */
const toFontAwesomeIcon = (icon: unknown): FontAwesomeIconProp =>
  icon as FontAwesomeIconProp;

const templateEditorIcons = {
  alignCenter: toFontAwesomeIcon(faAlignCenter),
  alignLeft: toFontAwesomeIcon(faAlignLeft),
  alignRight: toFontAwesomeIcon(faAlignRight),
  arrowDownToLine: toFontAwesomeIcon(faArrowDownToLine),
  arrowLeft: toFontAwesomeIcon(faArrowLeft),
  bringForward: toFontAwesomeIcon(faBringForward),
  bold: toFontAwesomeIcon(faBold),
  eye: toFontAwesomeIcon(faEye),
  eyeSlash: toFontAwesomeIcon(faEyeSlash),
  sendBackward: toFontAwesomeIcon(faSendBackward),
  italic: toFontAwesomeIcon(faItalic),
  lock: toFontAwesomeIcon(faLock),
  lockOpen: toFontAwesomeIcon(faLockOpen),
  plus: toFontAwesomeIcon(faPlus),
  trash: toFontAwesomeIcon(faTrash)
};

/** Language used for the editor interface. Use `en` for English or `de` for German. */
export type TemplateEditorLanguage = 'en' | 'de';

/** A font offered in the text-layer font picker. */
export type TemplateEditorFontOption = LomavisFontOption;

/** Functions supplied to `onExportActionsReady` for exports initiated outside the editor. */
export type TemplateEditorExportActions = {
  /** Sends the serialised template JSON to `onExportTemplate`. */
  exportTemplate: () => void;
  /** Creates a full-size PNG and sends it to `onExportImage`. */
  exportPng: () => Promise<File | undefined>;
  /** Creates a full-size JPEG and sends it to `onExportImage`. */
  exportJpg: () => Promise<File | undefined>;
  /** Creates a PNG thumbnail and sends it to `onExportImage`. */
  exportThumbnail: () => Promise<File | undefined>;
};

/** File type produced by the built-in Save button. Defaults to `json`. */
export type TemplateEditorSaveFormat = 'json' | 'png' | 'jpg' | 'thumbnail';

/** Data emitted after the built-in Save button completes. */
export type TemplateEditorSavePayload =
  | { format: 'json'; template: TemplateDefinition; json: string }
  | { format: 'png' | 'jpg' | 'thumbnail'; file: File; width?: number };

export type TemplateEditorUploadedImage = {
  src: string;
  uuid?: string;
};

const defaultFontOptions: TemplateEditorFontOption[] = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Verdana', label: 'Verdana' }
];

const BUNNY_FONTS_CSS_URL = 'https://fonts.bunny.net/css';
const loadedBunnyFontFamilies = new Set<string>();

const loadBunnyFontOptions = async (): Promise<TemplateEditorFontOption[]> => {
  const { default: catalogue } = await import(
    '../../../../pages/settings/branding/template_branding/webfonts.json'
  );
  return catalogue.items.map((font) => ({
    value: font.family,
    label: font.family
  }));
};

const loadBunnyFont = (
  font: TemplateEditorFontOption,
  onLoad?: () => void
): void => {
  if (
    loadedBunnyFontFamilies.has(font.value) ||
    typeof document === 'undefined'
  )
    return;

  loadedBunnyFontFamilies.add(font.value);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `${BUNNY_FONTS_CSS_URL}?family=${encodeURIComponent(
    font.value
  ).replace(/%20/g, '+')}:400,400i,700,700i&display=swap`;
  link.addEventListener('load', onLoad ?? (() => undefined));
  link.addEventListener('error', () =>
    loadedBunnyFontFamilies.delete(font.value)
  );
  document.head.append(link);
};

const getPinturaEditorDefaults = (language: TemplateEditorLanguage) =>
  getEditorDefaults({
    utils: ['annotate'],
    imageReader: createDefaultImageReader(),
    imageWriter: createDefaultImageWriter(),
    locale:
      language === 'de'
        ? { ...LocaleCoreDe, ...LocaleAnnotateDe, ...LocaleMarkupEditorDe }
        : { ...LocaleCore, ...LocaleAnnotate, ...LocaleMarkupEditor }
  });

export type LomavisTemplateEditorProps = {
  /** Current, application-owned template data. */
  value: TemplateDefinition;
  /** Receives every edit; update the `value` passed back to the editor. */
  onChange: (template: TemplateDefinition) => void;
  /** Receives the template and its serialised JSON when a JSON export occurs. */
  onExportTemplate?: (template: TemplateDefinition, json: string) => void;
  /** Receives an exported PNG, JPG, or thumbnail file without downloading it. */
  onExportImage?: (
    file: File,
    options: { format: 'png' | 'jpg' | 'thumbnail'; width?: number }
  ) => void;
  /** Receives export functions for buttons or actions outside the editor. */
  onExportActionsReady?: (actions: TemplateEditorExportActions) => void;
  /** Chooses the file type made by Save: JSON, PNG, JPG, or a PNG thumbnail. */
  saveFormat?: TemplateEditorSaveFormat;
  /** Receives the completed Save result in the format selected by `saveFormat`. */
  onSave?: (payload: TemplateEditorSavePayload) => void;
  /** Disables the Save action while the host is persisting the template. */
  isSaving?: boolean;
  /** Shows a back icon in the header and runs this callback when it is clicked. */
  onBack?: () => void;
  /** Accessible label for the back action. */
  backLabel?: string;
  /** Shows a header icon that immediately downloads the current canvas as a PNG. */
  showDownloadButton?: boolean;
  /** Optional upload handler. Resolve with the persistent image details to use after upload. */
  onUploadImage?: (
    file: File
  ) => Promise<string | TemplateEditorUploadedImage>;
  /** Interface language: `en` for English or `de` for German. Defaults to English. */
  language?: TemplateEditorLanguage;
  /** Extra fonts in the text picker, shown before the built-in fallback fonts. */
  fontOptions?: TemplateEditorFontOption[];
  /** Additional CSS class names for the editor's outer container. */
  className?: string;
  /** Hides the editable overlay name in embedded, per-image editing flows. */
  showTemplateName?: boolean;
  /** Shows Creator's simplified text controls before the remaining layers. */
  quickTextEditing?: boolean;
  /** Server-side validation feedback for the overlay name. */
  templateNameError?: string;
  /** Places the editor header and actions above or below the workspace. */
  headerPosition?: 'top' | 'bottom';
  /** Replaces canvas resizing controls with a read-only size label. */
  readOnlyCanvasSize?: boolean;
  /** Ref attached to the editor header. */
  headerRef?: React.Ref<HTMLElement>;
  /** Additional styles for the header's action group. */
  headerActionsStyle?: React.CSSProperties;
  /** Extra actions rendered alongside the editor's built-in header actions. */
  headerActions?: React.ReactNode;
};

/**
 * Draws the artboard as a white sheet against the grey workspace. Pintura
 * renders this inside its canvas, so it tracks the image exactly — a plain DOM
 * element cannot, as the image is positioned inside the WebGL canvas.
 */
const ARTBOARD_BACKGROUND = [1, 1, 1, 1];

/** Frozen at module scope: a fresh `[]` each render counts as a changed prop. */
const EMPTY_MARKUP_TOOLBAR: never[] = [];

/** Same reasoning, for an artboard that has no layers yet. */
const EMPTY_SHAPES: Shape[] = [];

/**
 * Whether Pintura's stylesheet — and our override of it — are both in effect
 * for the element Pintura is about to create.
 *
 * Pintura reads its root's computed colours once while starting up and seeds
 * its theme from what it finds, keeping a black default when the read comes
 * back without a usable colour. Nothing in the DOM reflects that afterwards, so
 * it can neither be detected nor repaired: the only safe moment to create the
 * editor is once the colours it will read are the ones we intend.
 *
 * Checking merely "not transparent" is too weak — Pintura's own default would
 * satisfy it while our override was still missing. So the probe is inserted
 * where Pintura's root will live and its resolved background is compared
 * against the workspace's own, which is the value both stylesheets are supposed
 * to agree on.
 */
const pinturaThemeReady = (workspace: HTMLElement | null) => {
  if (!workspace) return false;

  const probe = document.createElement('div');
  probe.className = 'PinturaRoot pintura-editor';
  probe.style.cssText =
    'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;visibility:hidden;pointer-events:none';
  workspace.appendChild(probe);

  const probeStyle = getComputedStyle(probe);
  const background = probeStyle.backgroundColor;
  // Pintura derives this from `--color-background`; an empty or non-colour
  // value means its stylesheet has not applied yet.
  const derived = probeStyle.getPropertyValue('--color-background-100').trim();
  probe.remove();

  const intended = getComputedStyle(workspace).backgroundColor;
  const isColour =
    !!background &&
    background !== 'transparent' &&
    background !== 'rgba(0, 0, 0, 0)';

  return isColour && background === intended && derived.startsWith('rgba(');
};

/** Longest the editor is withheld before it is mounted regardless. */
const ARTBOARD_MOUNT_TIMEOUT = 4000;

const createBackgroundSource = (
  width: number,
  height: number,
  color: string
): Blob =>
  new Blob(
    [
      color === 'transparent'
        ? `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"/>`
        : `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><rect width="100%" height="100%" fill="${color}"/></svg>`
    ],
    { type: 'image/svg+xml' }
  );

const getImageDimensions = async (
  file: File
): Promise<{ width: number; height: number }> =>
  await new Promise((resolve, reject) => {
    const source = URL.createObjectURL(file);
    const image = new Image();
    const cleanup = () => URL.revokeObjectURL(source);

    image.onload = () => {
      cleanup();
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      cleanup();
      reject(new Error('Unable to determine image dimensions'));
    };
    image.src = source;
  });

const getImageLayerBounds = (
  image: { width: number; height: number },
  canvas: TemplateDefinition['canvas'],
  maximumCanvasRatio = 0.8
) => {
  const scale = Math.min(
    (canvas.width * maximumCanvasRatio) / image.width,
    (canvas.height * maximumCanvasRatio) / image.height
  );
  const size = { width: image.width * scale, height: image.height * scale };

  return {
    size,
    position: {
      x: (canvas.width - size.width) / 2,
      y: (canvas.height - size.height) / 2
    }
  };
};

const getCanvasDimension = (value: string): number => {
  const dimension = Number(value);
  return Number.isFinite(dimension) && dimension > 0
    ? Math.max(1, Math.round(dimension))
    : 1;
};

type DropdownOption = {
  value: string;
  label: React.ReactNode;
};

const BootstrapDropdown: React.FC<{
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  ariaLabel: string;
}> = ({ value, options, onChange, disabled = false, ariaLabel }) => {
  return (
    <LomavisDropdown1
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled}
      ariaLabel={ariaLabel}
    />
  );
};

interface AddLayerToggleProps extends React.ComponentProps<typeof Button> {
  className?: string;
}

const AddLayerToggle = React.forwardRef<HTMLButtonElement, AddLayerToggleProps>(
  ({ className, children, ...props }, ref) => (
    <Button
      {...props}
      ref={ref}
      size="sm"
      variant="outline-primary"
      className={`d-inline-flex align-items-center justify-content-center rounded-3 p-0 fw-bold ${
        className?.replace('dropdown-toggle', '') ?? ''
      }`}
      style={{ width: 32, height: 32 }}
    >
      {children}
    </Button>
  )
);

AddLayerToggle.displayName = 'AddLayerToggle';

type EmptyTemplatePromptProps = {
  title: string;
  description: string;
  addLayerLabel: string;
  dropdownOpen: boolean;
  setDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  children: React.ReactNode;
};

const EmptyTemplatePrompt: React.FC<EmptyTemplatePromptProps> = ({
  title,
  description,
  addLayerLabel,
  dropdownOpen,
  setDropdownOpen,
  children
}) => (
  <div className="text-center text-dark">
    <div className="mb-2 fs-5 fw-bold">{title}</div>
    <p className="mb-3 text-muted">{description}</p>
    <Dropdown
      show={dropdownOpen}
      onToggle={(nextShow, metadata) => {
        if (metadata.source !== 'click') setDropdownOpen(nextShow);
      }}
    >
      <Dropdown.Toggle
        bsPrefix="lomavis-template-add-layer-toggle"
        size="sm"
        variant="primary"
        className="rounded-3 px-3 fw-semibold shadow-sm"
        aria-label={addLayerLabel}
        onClick={(event) => {
          event.stopPropagation();
          setDropdownOpen((isOpen) => !isOpen);
        }}
      >
        {addLayerLabel}
      </Dropdown.Toggle>
      <Dropdown.Menu className="lomavis-template-action-menu border-0 p-2 shadow">
        {children}
      </Dropdown.Menu>
    </Dropdown>
  </div>
);

type TemplateArtboardProps = {
  editorRef: React.RefObject<PinturaEditor>;
  defaults: ReturnType<typeof getPinturaEditorDefaults>;
  editorKey: string;
  src: Blob;
  annotation: Shape[];
  onReady: () => void;
  onLoadError: (error: unknown) => void;
  onSelectShape: (shape: Shape) => void;
  onUpdateShape: (shape: Shape) => void;
};

/**
 * Isolates Pintura from the host page's render loop.
 *
 * `@pqina/react-pintura` runs `Object.assign(this.editor, this.props)` on every
 * `componentDidUpdate`, and re-assigning the editor's inputs mid-load aborts
 * the image read already in flight. The consuming page re-renders several times
 * while the artboard is still loading — Redux updates, the location query
 * settling, translations arriving — and each of those used to reach the editor
 * and cancel the load, leaving an artboard that never appeared even though
 * Pintura still reported `ready`.
 *
 * Everything passed in therefore has to keep a stable identity across renders,
 * so this only updates when the artboard genuinely changed.
 */
const TemplateArtboard = React.memo(function TemplateArtboard(
  props: TemplateArtboardProps
) {
  const {
    editorRef,
    defaults,
    editorKey,
    src,
    annotation,
    onReady,
    onLoadError,
    onSelectShape,
    onUpdateShape
  } = props;

  return (
    <PinturaEditor
      {...defaults}
      key={editorKey}
      ref={editorRef}
      className="h-100"
      imageBackgroundColor={ARTBOARD_BACKGROUND}
      src={src}
      imageAnnotation={annotation}
      enableToolbar={false}
      enableUtils={false}
      enableZoom={false}
      enableZoomControls={false}
      enableZoomInput={false}
      enableTapToAddText={false}
      enableMoveTool
      enableAutoSelectMoveTool
      markupEditorToolbar={EMPTY_MARKUP_TOOLBAR}
      markupEditorToolRetainStyles={false}
      onReady={onReady}
      onLoaderror={onLoadError}
      onSelectshape={onSelectShape}
      onUpdateshape={onUpdateShape}
    />
  );
});

export const LomavisTemplateEditor: React.FC<LomavisTemplateEditorProps> = ({
  value,
  onChange,
  onBack,
  backLabel,
  onExportTemplate,
  onExportImage,
  onExportActionsReady,
  saveFormat = 'json',
  onSave,
  isSaving = false,
  showDownloadButton = false,
  onUploadImage,
  language = 'en',
  fontOptions = [],
  className,
  showTemplateName = true,
  quickTextEditing = false,
  templateNameError,
  headerPosition = 'top',
  readOnlyCanvasSize = false,
  headerRef,
  headerActionsStyle,
  headerActions
}) => {
  const { t: translate } = useTranslate();
  const t = useCallback(
    (key: string, defaultValue: string) =>
      translate({ key, defaultValue, language }),
    [language, translate]
  );
  const resolvedBackLabel =
    backLabel ?? t('templateEditor.actions.back', 'Back');
  const editorRef = useRef<PinturaEditor>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const initializationErrorKeyRef = useRef<string | null>(null);
  const valueRef = useRef(value);
  const previousTemplateIdRef = useRef(value.id);
  const onExportTemplateRef = useRef(onExportTemplate);
  const onExportImageRef = useRef(onExportImage);
  const onSaveRef = useRef(onSave);
  valueRef.current = value;
  onExportTemplateRef.current = onExportTemplate;
  onExportImageRef.current = onExportImage;
  onSaveRef.current = onSave;
  const [tab, setTab] = useState('layers');
  const [canMountArtboard, setCanMountArtboard] = useState(false);
  const [readyPinturaEditorKey, setReadyPinturaEditorKey] = useState<
    string | null
  >(null);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(
    value.layers[0]?.id ?? null
  );
  const [templateName, setTemplateName] = useState(value.name ?? '');
  const templateNameRef = useRef(templateName);
  templateNameRef.current = templateName;
  const layerShapeCacheRef = useRef<
    Map<
      string,
      {
        layer: TemplateLayer;
        base: TemplatePinturaShape;
      }
    >
  >(new Map());
  const shapeCacheFontRevisionRef = useRef(0);
  const [hasTemplateNameError, setHasTemplateNameError] = useState(false);
  const [selectionRevision, setSelectionRevision] = useState(0);
  const [fontLoadRevision, setFontLoadRevision] = useState(0);
  const [emptyAddLayerDropdownOpen, setEmptyAddLayerDropdownOpen] =
    useState(false);
  const [emptySidebarAddLayerDropdownOpen, setEmptySidebarAddLayerDropdownOpen] =
    useState(false);
  const [sidebarAddLayerDropdownOpen, setSidebarAddLayerDropdownOpen] =
    useState(false);
  const [bunnyFontOptions, setBunnyFontOptions] = useState<
    TemplateEditorFontOption[]
  >([]);
  const [hasLoadedBunnyFontOptions, setHasLoadedBunnyFontOptions] =
    useState(false);
  const selectedLayer = value.layers.find(
    (layer) => layer.id === selectedLayerId
  );
  const textLayers = quickTextEditing
    ? value.layers.filter((layer) => layer.kind === 'text')
    : [];
  const sidebarLayers = quickTextEditing
    ? value.layers.filter((layer) => layer.kind !== 'text')
    : value.layers;
  const nameValidationMessage = hasTemplateNameError
    ? t(
        'templateEditor.templateName.required',
        'Please name your template before saving.'
      )
    : templateNameError;
  useEffect(() => {
    setTemplateName(value.name ?? '');
  }, [value.name]);
  useEffect(() => {
    const templateChanged = previousTemplateIdRef.current !== value.id;
    previousTemplateIdRef.current = value.id;
    if (templateChanged) {
      setHasTemplateNameError(false);
    }
    setSelectedLayerId((currentId) => {
      if (
        !templateChanged &&
        currentId &&
        value.layers.some((layer) => layer.id === currentId)
      ) {
        return currentId;
      }
      return value.layers[0]?.id ?? null;
    });
    if (templateChanged || value.layers.length === 0) setTab('layers');
  }, [value.id, value.layers]);
  const shapes = useMemo(
    () => {
      if (value.layers.length === 0) return EMPTY_SHAPES;
      if (shapeCacheFontRevisionRef.current !== fontLoadRevision) {
        layerShapeCacheRef.current.clear();
        shapeCacheFontRevisionRef.current = fontLoadRevision;
      }
      return value.layers.map((layer) => {
        const cached = layerShapeCacheRef.current.get(layer.id);
        const base =
          cached?.layer === layer ? cached.base : toPinturaShape(layer);
        if (cached?.layer !== layer)
          layerShapeCacheRef.current.set(layer.id, { layer, base });
        return {
          ...base,
          isSelected: layer.id === selectedLayerId && !base.disableSelect
        };
      });
    },
    [fontLoadRevision, selectedLayerId, selectionRevision, value.layers]
  );
  const pinturaEditorDefaults = useMemo(
    () => getPinturaEditorDefaults(language),
    [language]
  );
  const pinturaEditorKey = `${value.canvas.width}x${value.canvas.height}`;
  const artboardSource = useMemo(
    () =>
      createBackgroundSource(
        value.canvas.width,
        value.canvas.height,
        value.canvas.backgroundColor
      ),
    [value.canvas.backgroundColor, value.canvas.height, value.canvas.width]
  );
  const isPinturaReady = readyPinturaEditorKey === pinturaEditorKey;

  // Stable handler identities. `selectLayer`/`updateShape` are redefined on
  // every render, and every changed prop reaches the live editor (see
  // `TemplateArtboard`), so they are routed through refs instead.
  const selectLayerRef = useRef<
    (id: string, openEdit?: boolean, forceFullEdit?: boolean) => void
  >();
  const updateShapeRef = useRef<(shape: Shape) => void>();
  const handleArtboardReady = useCallback(
    () => setReadyPinturaEditorKey(pinturaEditorKey),
    [pinturaEditorKey]
  );
  const handleArtboardLoadError = useCallback((error: unknown) => {
    console.error(
      '[LomavisTemplateEditor] Pintura could not load the local SVG artboard.',
      { error, canvas: valueRef.current.canvas }
    );
  }, []);
  const handleArtboardSelectShape = useCallback((shape: Shape) => {
    const id = (shape as Shape & { id?: string }).id;
    if (id) selectLayerRef.current?.(id, true);
  }, []);
  const handleArtboardUpdateShape = useCallback((shape: Shape) => {
    updateShapeRef.current?.(shape);
  }, []);
  // Hold the editor back until its container is laid out and Pintura's own
  // stylesheet has applied, so it can never read its theme from an unstyled or
  // zero-sized root. The workspace stays rendered and sized throughout: the
  // loading state is painted over it, never in place of it, since replacing it
  // would recreate the very condition this guards against.
  //
  // Driven by timers, not `requestAnimationFrame`: rAF is paused in a
  // background tab, which would leave the editor waiting indefinitely and would
  // never reach a ceiling checked from inside the same loop.
  useEffect(() => {
    if (canMountArtboard) return;

    let cancelled = false;
    let stableChecks = 0;
    let lastHeight = -1;

    const check = () => {
      if (cancelled) return;
      const rect = workspaceRef.current?.getBoundingClientRect();
      const width = rect ? Math.round(rect.width) : 0;
      const height = rect ? Math.round(rect.height) : 0;

      if (width > 0 && height > 0 && height === lastHeight) stableChecks += 1;
      else {
        stableChecks = 0;
        lastHeight = height;
      }

      if (stableChecks >= 2 && pinturaThemeReady(workspaceRef.current)) {
        window.clearInterval(interval);
        window.clearTimeout(ceiling);
        setCanMountArtboard(true);
      }
    };

    const interval = window.setInterval(check, 100);
    const ceiling = window.setTimeout(() => {
      window.clearInterval(interval);
      if (cancelled) return;
      console.warn(
        '[LomavisTemplateEditor] Mounting the artboard after ' +
          `${ARTBOARD_MOUNT_TIMEOUT}ms without a settled container, or without ` +
          'the editor stylesheets resolving to the intended colours.'
      );
      setCanMountArtboard(true);
    }, ARTBOARD_MOUNT_TIMEOUT);

    check();

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.clearTimeout(ceiling);
    };
  }, [canMountArtboard]);
  useEffect(() => {
    if (isPinturaReady) return;

    const timeout = window.setTimeout(() => {
      if (initializationErrorKeyRef.current === pinturaEditorKey) return;
      initializationErrorKeyRef.current = pinturaEditorKey;
      const { width = 0, height = 0 } =
        workspaceRef.current?.getBoundingClientRect() ?? {};
      console.error(
        '[LomavisTemplateEditor] Pintura failed to initialize within 8 seconds.',
        {
          canvas: valueRef.current.canvas,
          workspace: { width, height },
        }
      );
    }, 8000);

    return () => window.clearTimeout(timeout);
  }, [isPinturaReady, pinturaEditorKey]);
  const availableFontOptions = useMemo(() => {
    const byValue = new Map<string, TemplateEditorFontOption>();
    [...fontOptions, ...bunnyFontOptions, ...defaultFontOptions].forEach(
      (font) => {
        if (!byValue.has(font.value)) byValue.set(font.value, font);
      }
    );
    return [...byValue.values()];
  }, [bunnyFontOptions, fontOptions]);
  const ensureBunnyFont = useCallback((font: TemplateEditorFontOption) => {
    loadBunnyFont(font, () => setFontLoadRevision((revision) => revision + 1));
  }, []);
  const loadFontCatalogue = useCallback(() => {
    if (hasLoadedBunnyFontOptions) return;
    setHasLoadedBunnyFontOptions(true);
    void loadBunnyFontOptions()
      .then(setBunnyFontOptions)
      .catch(() => setHasLoadedBunnyFontOptions(false));
  }, [hasLoadedBunnyFontOptions]);
  useEffect(() => {
    const fontsByFamily = new Map(
      availableFontOptions.map((font) => [font.value, font])
    );
    value.layers.forEach((layer) => {
      if (layer.kind === 'text') {
        ensureBunnyFont(
          fontsByFamily.get(layer.typography.fontFamily) ?? {
            value: layer.typography.fontFamily,
            label: layer.typography.fontFamily
          }
        );
      }
    });
  }, [availableFontOptions, ensureBunnyFont, value.layers]);
  const update = (
    updater: (template: TemplateDefinition) => TemplateDefinition
  ) => onChange(updater(valueRef.current));
  const updateLayer = (
    id: string,
    updater: (layer: TemplateLayer) => TemplateLayer
  ) =>
    update((template) => ({
      ...template,
      layers: template.layers.map((layer) =>
        layer.id === id ? updater(layer) : layer
      )
    }));
  const selectLayer = (
    id: string,
    openEdit = false,
    forceFullEdit = false
  ) => {
    setSelectedLayerId(id);
    // Re-apply selection even when this row was already active but Pintura's
    // canvas selection was cleared independently.
    setSelectionRevision((revision) => revision + 1);
    const layer = valueRef.current.layers.find((current) => current.id === id);
    if (quickTextEditing && layer?.kind === 'text' && !forceFullEdit) {
      setTab('layers');
      return;
    }
    if (openEdit) setTab('edit');
  };
  const backgroundExists = hasBackgroundLayer(value.layers);
  const addLayer = (
    kind: TemplateLayerKind,
    shapeType?: TemplateLayer['shapeType']
  ) => {
    if (kind === 'background' && backgroundExists) {
      toast(
        t(
          'templateEditor.background.alreadyExists',
          'A template can only have one background layer.'
        )
      );
      return;
    }
    const layer = createLayer(kind, value.canvas, shapeType);
    update((template) => ({
      ...template,
      // The background enters at the bottom of the stack and stays there.
      layers:
        kind === 'background'
          ? [layer, ...template.layers]
          : [...template.layers, layer]
    }));
    selectLayer(layer.id, true);
  };
  const updateShape = (shape: Shape) => {
    const id = (shape as Shape & { id?: string }).id;
    if (!id) return;
    const target = value.layers.find((layer) => layer.id === id);
    if (!target) return;
    updateLayer(id, (layer) => ({
      ...layer,
      ...pinturaShapeToLayerPatch(shape, value.canvas, layer)
    }));
  };
  const moveLayer = (direction: -1 | 1) => {
    if (!selectedLayerId) return;
    update((template) => {
      const index = template.layers.findIndex(
        (layer) => layer.id === selectedLayerId
      );
      const target = index + direction;
      if (index < 0 || target < 0 || target >= template.layers.length)
        return template;
      if (template.layers[index].locked || template.layers[target].locked)
        return template;
      // Nothing swaps with the background: it neither rises nor gets buried.
      if (
        template.layers[index].kind === 'background' ||
        template.layers[target].kind === 'background'
      )
        return template;
      const layers = [...template.layers];
      [layers[index], layers[target]] = [layers[target], layers[index]];
      return { ...template, layers };
    });
  };
  const currentTemplate = useCallback(() => {
    const name = templateNameRef.current.trim();
    return showTemplateName && name !== valueRef.current.name
      ? { ...valueRef.current, name }
      : valueRef.current;
  }, [showTemplateName]);
  const exportTemplate = useCallback(() => {
    const template = currentTemplate();
    onExportTemplateRef.current?.(template, serializeTemplate(template));
  }, [currentTemplate]);
  const exportImage = useCallback(
    async (
      mimeType: 'image/png' | 'image/jpeg',
      format: 'png' | 'jpg' | 'thumbnail',
      width?: number
    ) => {
      try {
        const processingOptions: PinturaEditorOptions = {
          imageReader: createDefaultImageReader(),
          imageWriter: createDefaultImageWriter({
            mimeType,
            quality: 0.92,
            targetSize: width ? { width, upscale: false } : undefined
          }),
          // JPEG cannot preserve transparency, so its empty pixels use the same
          // white background that users see on the editor artboard.
          imageBackgroundColor:
            mimeType === 'image/jpeg' ? ARTBOARD_BACKGROUND : undefined,
          locale: locale_en_gb
        };
        // Pintura applies writer overrides from the first argument when it is
        // processing the currently loaded image. Passing these as the second
        // argument is only honored while loading a new source.
        const result = await editorRef.current?.editor.processImage(
          processingOptions as unknown as ImageSource
        );
        if (result?.dest) {
          onExportImageRef.current?.(result.dest, { format, width });
          return result.dest;
        }
      } catch (error) {
        window.alert(
          error instanceof Error
            ? error.message
            : t(
                'templateEditor.errors.export',
                'Unable to export this overlay.'
              )
        );
      }
      return undefined;
    },
    [t]
  );
  selectLayerRef.current = selectLayer;
  updateShapeRef.current = updateShape;

  const exportActions = useMemo<TemplateEditorExportActions>(
    () => ({
      exportTemplate,
      exportPng: async () => await exportImage('image/png', 'png'),
      exportJpg: async () => await exportImage('image/jpeg', 'jpg'),
      exportThumbnail: async () =>
        await exportImage(
          'image/png',
          'thumbnail',
          TEMPLATE_EDITOR_THUMBNAIL_WIDTH
        )
    }),
    [exportImage, exportTemplate]
  );
  useEffect(() => {
    onExportActionsReady?.(exportActions);
  }, [exportActions, onExportActionsReady]);
  const save = useCallback(async () => {
    const name = templateName.trim();
    if (showTemplateName && !name) {
      setHasTemplateNameError(true);
      toast.error(
        t(
          'templateEditor.templateName.required',
          'Please name your template before saving.'
        )
      );
      return;
    }
    if (valueRef.current.layers.length === 0) {
      toast.error(
        t(
          'templateEditor.layers.required',
          'Add at least one layer before saving.'
        )
      );
      return;
    }
    const template = currentTemplate();
    if (template !== valueRef.current) {
      setTemplateName(name);
      onChange(template);
    }
    try {
      if (saveFormat === 'json') {
        const json = serializeTemplate(template);
        onExportTemplateRef.current?.(template, json);
        onSaveRef.current?.({ format: 'json', template, json });
        return;
      }
      const imageOptions: {
        mimeType: 'image/png' | 'image/jpeg';
        format: 'png' | 'jpg' | 'thumbnail';
        width?: number;
      } =
        saveFormat === 'jpg'
          ? { mimeType: 'image/jpeg' as const, format: 'jpg' as const }
          : saveFormat === 'thumbnail'
          ? {
              mimeType: 'image/png' as const,
              format: 'thumbnail' as const,
              width: TEMPLATE_EDITOR_THUMBNAIL_WIDTH
            }
          : { mimeType: 'image/png' as const, format: 'png' as const };
      const file = await exportImage(
        imageOptions.mimeType,
        imageOptions.format,
        imageOptions.width
      );
      if (file) {
        onSaveRef.current?.({
          format: imageOptions.format,
          file,
          width: imageOptions.width
        });
      }
    } catch (error) {
      window.alert(
        error instanceof Error
          ? error.message
          : t('templateEditor.errors.save', 'Unable to save this overlay.')
      );
    }
  }, [currentTemplate, exportImage, onChange, saveFormat, showTemplateName, t, templateName]);
  const downloadPng = useCallback(async () => {
    const file = await exportImage('image/png', 'png');
    if (!file) return;

    const link = document.createElement('a');
    const filename = currentTemplate().name?.trim() || 'Overlay 1';
    link.href = URL.createObjectURL(file);
    link.download = `${filename.replace(/[\\/:*?"<>|]/g, '-')}.png`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  }, [currentTemplate, exportImage]);
  const replaceImage = async (file?: File): Promise<void> => {
    if (!file || !selectedLayer) return;
    const imageDimensions = await getImageDimensions(file).catch(
      () => undefined
    );
    const setImageSource = ({ src, uuid }: TemplateEditorUploadedImage) =>
      updateLayer(selectedLayer.id, (layer) => ({
        ...layer,
        ...(layer.kind === 'background'
          ? {
              position: { x: 0, y: 0 },
              size: {
                width: valueRef.current.canvas.width,
                height: valueRef.current.canvas.height
              }
            }
          : layer.kind === 'image' && imageDimensions
          ? getImageLayerBounds(imageDimensions, valueRef.current.canvas)
          : {}),
        image: {
          ...layer.image,
          fit:
            layer.image?.fit ??
            (layer.kind === 'background' ? 'cover' : 'contain'),
          src,
          uuid
        }
      }));

    if (onUploadImage) {
      try {
        const uploadedImage = await onUploadImage(file);
        setImageSource(
          typeof uploadedImage === 'string'
            ? { src: uploadedImage }
            : uploadedImage
        );
      } catch {
        toast.error(
          t(
            'templateEditor.errors.imageUpload',
            'Unable to upload the image. You can keep editing and try again.'
          )
        );
      }
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImageSource({ src: String(reader.result) });
    reader.onerror = () =>
      toast.error(
        t(
          'templateEditor.errors.imageRead',
          'Unable to read the image. You can keep editing and try again.'
        )
      );
    reader.readAsDataURL(file);
  };
  const setCanvas = (presetId: string) => {
    if (presetId === 'custom')
      return update((template) => ({
        ...template,
        canvas: { ...template.canvas, preset: 'custom' }
      }));
    const preset = canvasPresets.find((item) => item.id === presetId);
    if (preset)
      update((template) =>
        resizeTemplateCanvas(template, preset.width, preset.height, preset.id)
      );
  };
  const addLayerOptions: Array<{
    kind: TemplateLayerKind;
    title: string;
    subtitle: string;
  }> = [
    {
      kind: 'text',
      title: t('templateEditor.actions.addTextLayer', 'Text layer'),
      subtitle: t(
        'templateEditor.actions.addTextLayerDescription',
        'Add editable text to your design.'
      )
    },
    {
      kind: 'shape',
      title: t('templateEditor.actions.addShapeLayer', 'Shape layer'),
      subtitle: t(
        'templateEditor.actions.addShapeLayerDescription',
        'Add a rectangle, circle, or line.'
      )
    },
    {
      kind: 'image',
      title: t('templateEditor.actions.addImageLayer', 'Image layer'),
      subtitle: t(
        'templateEditor.actions.addImageLayerDescription',
        'Upload and position an image.'
      )
    },
    {
      kind: 'background',
      title: t('templateEditor.actions.addBackgroundLayer', 'Background layer'),
      subtitle: t(
        'templateEditor.actions.addBackgroundLayerDescription',
        'Set the canvas color or background image.'
      )
    }
  ];
  const renderAddLayerOptions = (closeDropdown: () => void) =>
    addLayerOptions.map(({ kind, title, subtitle }) => (
      <Dropdown.Item
        key={kind}
        onClick={() => {
          addLayer(kind);
          closeDropdown();
        }}
      >
        <span className="lomavis-template-add-layer-title">{title}</span>
        <span className="lomavis-template-add-layer-subtitle">{subtitle}</span>
      </Dropdown.Item>
    ));

  const editorHeader = (
    <header
      ref={headerRef}
      className={`d-flex flex-wrap align-items-center gap-2 p-3 ${
        headerPosition === 'bottom' ? 'border-top' : 'border-bottom'
      }`}
    >
        {onBack && (
          <LomavisOverlayToolTip1
            placement="bottom"
            creatorStyle
            text={resolvedBackLabel}
          >
            <Button
              type="button"
              size="sm"
              variant="light"
              className="lomavis-template-layer-action bg-white text-gray-700"
              aria-label={resolvedBackLabel}
              onClick={onBack}
            >
              <FontAwesomeIcon
                icon={templateEditorIcons.arrowLeft}
                aria-hidden="true"
              />
            </Button>
          </LomavisOverlayToolTip1>
        )}
        {showTemplateName && (
          <Form.Group style={{ width: 345, maxWidth: '100%' }}>
            <Form.Control
              aria-label={t('templateEditor.templateName', 'Overlay name')}
              aria-invalid={Boolean(nameValidationMessage)}
              className="fw-semibold"
              isInvalid={Boolean(nameValidationMessage)}
              value={templateName}
              placeholder={t(
                'templateEditor.templateName.placeholder',
                'Name'
              )}
              onChange={(event) => {
                const name = event.target.value;
                if (name.trim()) setHasTemplateNameError(false);
                setTemplateName(name);
              }}
            />
            {nameValidationMessage && (
              <Form.Control.Feedback type="invalid">
                {nameValidationMessage}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        )}
      {readOnlyCanvasSize ? (
          <div
            aria-label={t('templateEditor.canvasSize', 'Canvas size')}
            className="px-2 text-muted small"
          >
            {value.canvas.width} × {value.canvas.height}
          </div>
        ) : (
          <Form.Group style={{ width: 250, maxWidth: '100%' }}>
            <BootstrapDropdown
              value={value.canvas.preset}
              ariaLabel={t('templateEditor.canvasSize', 'Canvas size')}
              onChange={setCanvas}
              options={[
                ...canvasPresets.map((preset) => ({
                  value: preset.id,
                  label: `${t(
                    `templateEditor.presets.${preset.id}`,
                    preset.label
                  )} · ${preset.width} × ${preset.height}`
                })),
                {
                  value: 'custom',
                  label: t('templateEditor.presets.custom', 'Custom…')
                }
              ]}
            />
            {value.canvas.preset === 'custom' && (
              <InputGroup className="mt-2">
                <Form.Control
                  type="number"
                  min="1"
                  value={value.canvas.width}
                  aria-label={t('templateEditor.canvasWidth', 'Canvas width')}
                  onChange={(event) =>
                    update((template) =>
                      resizeTemplateCanvas(
                        template,
                        getCanvasDimension(event.target.value),
                        template.canvas.height,
                        'custom'
                      )
                    )
                  }
                />
                <InputGroup.Text>×</InputGroup.Text>
                <Form.Control
                  type="number"
                  min="1"
                  value={value.canvas.height}
                  aria-label={t('templateEditor.canvasHeight', 'Canvas height')}
                  onChange={(event) =>
                    update((template) =>
                      resizeTemplateCanvas(
                        template,
                        template.canvas.width,
                        getCanvasDimension(event.target.value),
                        'custom'
                      )
                    )
                  }
                />
              </InputGroup>
            )}
          </Form.Group>
        )}
        {isPinturaReady && (
          <div
            className="ms-auto d-flex align-items-center gap-2"
            style={headerActionsStyle}
          >
            {headerActions}
            {showDownloadButton && (
              <LomavisOverlayToolTip1
                placement="bottom"
                creatorStyle
                text={t(
                  'templateEditor.actions.downloadPng',
                  'Download PNG'
                )}
              >
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="lomavis-template-layer-action bg-white text-gray-700"
                  aria-label={t(
                    'templateEditor.actions.downloadPng',
                    'Download PNG'
                  )}
                  onClick={() => {
                    void downloadPng();
                  }}
                >
                  <FontAwesomeIcon
                    icon={templateEditorIcons.arrowDownToLine}
                    aria-hidden="true"
                  />
                </Button>
              </LomavisOverlayToolTip1>
            )}
            <Button
              size="sm"
              variant="primary"
              className="rounded-3 px-4 fw-semibold shadow-sm"
              disabled={isSaving}
            onClick={() => {
              void save();
            }}
          >
            {isSaving
              ? t('templateEditor.actions.saving', 'Saving…')
              : t('templateEditor.actions.save', 'Save')}
          </Button>
        </div>)}
    </header>
  );

  return (
    <div
      className={`lomavis-template-editor d-flex w-100 flex-column overflow-hidden border rounded-3 bg-white ${
        className ?? ''
      }`}
      style={{
        color: 'var(--bs-gray-900, #071437)',
        fontFamily: 'Inter, Helvetica, sans-serif'
      }}
    >
      {headerPosition === 'top' && editorHeader}
      <main
        className="d-grid flex-grow-1 overflow-hidden"
        style={{
          gridTemplateColumns: 'minmax(0, 1fr) 360px',
          minHeight: 0,
          flexBasis: 0
        }}
      >
        <section
          className="lomavis-template-workspace d-flex flex-column overflow-hidden"
          style={{ minWidth: 0, minHeight: 0 }}
        >
          <div
            ref={workspaceRef}
            className="position-relative flex-grow-1"
            style={{ minHeight: 0, touchAction: 'none' }}
          >
            <div className="position-relative h-100">
              {canMountArtboard && (
                <TemplateArtboard
                  editorRef={editorRef}
                  defaults={pinturaEditorDefaults}
                  editorKey={pinturaEditorKey}
                  src={artboardSource}
                  annotation={shapes}
                  onReady={handleArtboardReady}
                  onLoadError={handleArtboardLoadError}
                  onSelectShape={handleArtboardSelectShape}
                  onUpdateShape={handleArtboardUpdateShape}
                />
              )}
            </div>
            {!isPinturaReady && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{
                  zIndex: 3,
                  background:
                    'rgb(var(--lomavis-template-workspace-bg, 245, 245, 247))'
                }}
                role="status"
                aria-live="polite"
              >
                <span
                  className="spinner-border text-secondary"
                  style={{ width: '1.75rem', height: '1.75rem' }}
                />
                <span className="visually-hidden">
                  {t('templateEditor.loading', 'Preparing the editor…')}
                </span>
              </div>
            )}
            {isPinturaReady && value.layers.length === 0 && (
              <div
                className="position-absolute top-50 start-50 translate-middle"
                style={{ width: 280, zIndex: 2 }}
              >
                <EmptyTemplatePrompt
                  title={t('templateEditor.empty.title', 'Start your overlay')}
                  description={t(
                    'templateEditor.empty.description',
                    'Add a layer to start building your design.'
                  )}
                  addLayerLabel={t('templateEditor.actions.add', 'Add layer')}
                  dropdownOpen={emptyAddLayerDropdownOpen}
                  setDropdownOpen={setEmptyAddLayerDropdownOpen}
                >
                  {renderAddLayerOptions(() =>
                    setEmptyAddLayerDropdownOpen(false)
                  )}
                </EmptyTemplatePrompt>
              </div>
            )}
          </div>
        </section>
        <aside
          className="d-flex h-100 flex-column overflow-hidden border-start bg-white"
          style={{ minHeight: 0 }}
        >
          <div className="d-flex flex-shrink-0 align-items-center gap-2 border-bottom bg-white px-3 py-2">
            <div
              role="tablist"
              aria-label={t(
                'templateEditor.sidebarLabel',
                'Overlay editor sidebar'
              )}
              className="d-flex flex-grow-1 rounded-3 bg-light p-1"
            >
              <Button
                size="sm"
                variant="light"
                role="tab"
                aria-selected={tab === 'layers'}
                className={`flex-grow-1 rounded-3 border-0 py-1 fw-medium ${
                  tab === 'layers'
                    ? 'bg-white text-dark shadow-sm'
                    : 'bg-transparent text-muted shadow-none'
                }`}
                onClick={() => setTab('layers')}
              >
                {t('templateEditor.tabs.layers', 'Layers')}
              </Button>
              <Button
                size="sm"
                variant="light"
                role="tab"
                aria-selected={tab === 'edit'}
                disabled={!selectedLayer}
                className={`flex-grow-1 rounded-3 border-0 py-1 fw-medium ${
                  tab === 'edit'
                    ? 'bg-white text-dark shadow-sm'
                    : 'bg-transparent text-muted shadow-none'
                }`}
                onClick={() => setTab('edit')}
              >
                {t('templateEditor.tabs.editLayer', 'Edit layer')}
              </Button>
            </div>
            <Dropdown
              align="end"
              show={sidebarAddLayerDropdownOpen}
              onToggle={(nextShow, metadata) => {
                if (metadata.source !== 'click')
                  setSidebarAddLayerDropdownOpen(nextShow);
              }}
            >
              <Dropdown.Toggle
                as={AddLayerToggle}
                aria-label={t('templateEditor.actions.add', 'Add layer')}
                onClick={(event) => {
                  event.stopPropagation();
                  setSidebarAddLayerDropdownOpen((isOpen) => !isOpen);
                }}
              >
                <FontAwesomeIcon
                  icon={templateEditorIcons.plus}
                  aria-hidden="true"
                />
              </Dropdown.Toggle>
              <Dropdown.Menu
                align="end"
                flip={false}
                className="lomavis-template-action-menu border-0 p-2 shadow"
              >
                {renderAddLayerOptions(() =>
                  setSidebarAddLayerDropdownOpen(false)
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
          {tab === 'layers' && value.layers.length === 0 ? (
            <div className="d-flex flex-grow-1 align-items-center justify-content-center p-3 text-center">
              <EmptyTemplatePrompt
                title={t('templateEditor.empty.title', 'Start your overlay')}
                description={t(
                  'templateEditor.empty.description',
                  'Add a layer to start building your design.'
                )}
                addLayerLabel={t('templateEditor.actions.add', 'Add layer')}
                dropdownOpen={emptySidebarAddLayerDropdownOpen}
                setDropdownOpen={setEmptySidebarAddLayerDropdownOpen}
              >
                {renderAddLayerOptions(() =>
                  setEmptySidebarAddLayerDropdownOpen(false)
                )}
              </EmptyTemplatePrompt>
            </div>
          ) : (
            <SimpleBar
              forceVisible="y"
              autoHide={false}
              className="lomavis-template-sidebar-scroll flex-grow-1"
              style={{ minHeight: 0, height: '100%', flexBasis: 0 }}
            >
              {tab === 'layers' ? (
                <div>
                  {textLayers.length > 0 && (
                    <QuickTextLayerFields
                      layers={textLayers}
                      language={language}
                      onUpdate={(id, updater) => updateLayer(id, updater)}
                      onFocusLayer={(id) => selectLayer(id)}
                      onMoreOptions={(id) => selectLayer(id, true, true)}
                    />
                  )}
                  <div className="lomavis-template-layer-list p-2">
                  {sidebarLayers.map((layer) => (
                    <div
                      role="button"
                      tabIndex={0}
                      key={layer.id}
                      className={`lomavis-template-layer-list-item d-grid w-100 align-items-center gap-2 p-2 text-start ${
                        selectedLayerId === layer.id
                          ? 'is-selected'
                          : ''
                      }`}
                      style={{
                        gridTemplateColumns: '28px minmax(0, 1fr) 28px 28px'
                      }}
                      onClick={() => selectLayer(layer.id, true)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          selectLayer(layer.id, true);
                        }
                      }}
                      >
                      <span
                        className={`lomavis-template-layer-kind lomavis-template-layer-kind-${layer.kind}`}
                      >
                        {layer.kind === 'text'
                          ? 'T'
                          : layer.kind === 'image'
                          ? '▧'
                          : layer.kind === 'background'
                          ? '▣'
                          : '□'}
                      </span>
                      <span className="text-truncate fw-semibold">
                        {layer.name}
                      </span>
                      <Button
                        type="button"
                        variant="link"
                        className="lomavis-template-layer-list-action"
                        aria-label={t(
                          'templateEditor.actions.toggleVisibility',
                          'Toggle layer visibility'
                        )}
                        onClick={(event) => {
                          event.stopPropagation();
                          updateLayer(layer.id, (current) => ({
                            ...current,
                            visible: !current.visible
                          }));
                        }}
                      >
                        <FontAwesomeIcon
                          icon={
                            layer.visible
                              ? templateEditorIcons.eye
                              : templateEditorIcons.eyeSlash
                          }
                          aria-hidden="true"
                        />
                      </Button>
                      <Button
                        type="button"
                        variant="link"
                        className="lomavis-template-layer-list-action"
                        aria-label={t(
                          'templateEditor.actions.toggleLock',
                          'Toggle layer lock'
                        )}
                        onClick={(event) => {
                          event.stopPropagation();
                          updateLayer(layer.id, (current) => {
                            const locked = !current.locked;
                            return {
                              ...current,
                              locked,
                              ...(current.kind === 'text' && !locked
                                ? { textLocked: false }
                                : {})
                            };
                          });
                        }}
                      >
                        <FontAwesomeIcon
                          icon={
                            layer.locked
                              ? templateEditorIcons.lock
                              : templateEditorIcons.lockOpen
                          }
                          aria-hidden="true"
                        />
                      </Button>
                    </div>
                  ))}
                  </div>
                </div>
              ) : selectedLayer ? (
                <LayerFields
                  layer={selectedLayer}
                  language={language}
                  fontOptions={availableFontOptions}
                  showTextLock={!quickTextEditing}
                  allowTextDetailsWhenLayerLocked={quickTextEditing}
                  onFontSelect={ensureBunnyFont}
                  onFontPickerFocus={loadFontCatalogue}
                  onUpdate={(updater) => updateLayer(selectedLayer.id, updater)}
                  onMove={moveLayer}
                  onDelete={() => {
                    update((template) => ({
                      ...template,
                      layers: template.layers.filter(
                        (layer) => layer.id !== selectedLayer.id
                      )
                    }));
                    setSelectedLayerId(
                      value.layers.find(
                        (layer) => layer.id !== selectedLayer.id
                      )?.id ?? null
                    );
                    setTab('layers');
                  }}
                  onImageSelect={async (file) => {
                    await replaceImage(file);
                  }}
                  onImageRemove={() =>
                    updateLayer(selectedLayer.id, (layer) => ({
                      ...layer,
                      image: {
                        ...layer.image,
                        fit: layer.kind === 'background' ? 'cover' : 'contain',
                        src: undefined,
                        uuid: undefined
                      }
                    }))
                  }
                />
              ) : null}
            </SimpleBar>
          )}
          {tab === 'edit' && selectedLayer && (
            <div className="flex-shrink-0 border-top bg-white p-3">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="d-flex w-100 align-items-center justify-content-center gap-2"
                onClick={() => setTab('layers')}
              >
                <FontAwesomeIcon
                  className="fs-3"
                  icon={templateEditorIcons.arrowLeft}
                  aria-hidden="true"
                />
                {t('templateEditor.actions.backToLayers', 'Back to layers')}
              </Button>
            </div>
          )}
        </aside>
      </main>
      {headerPosition === 'bottom' && editorHeader}
    </div>
  );
};

const MINIMUM_TEXT_FONT_SIZE = 8;

const getCanvasFont = (
  typography: NonNullable<TemplateLayer['typography']>,
  fontSize: number
) =>
  `${typography.fontStyle} ${typography.fontWeight} ${fontSize}px "${typography.fontFamily.replace(/"/g, '\\"')}"`;

const measureTextWidth = (
  context: CanvasRenderingContext2D,
  text: string,
  letterSpacing: number
) =>
  context.measureText(text).width +
  Math.max(0, text.length - 1) * letterSpacing;

const getWrappedTextLines = (
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  letterSpacing: number
) =>
  text.split('\n').flatMap((paragraph) => {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (words.length === 0) return [''];

    const lines: string[] = [];
    let line = '';
    words.forEach((word) => {
      const candidate = line ? `${line} ${word}` : word;
      if (
        line &&
        measureTextWidth(context, candidate, letterSpacing) > maxWidth
      ) {
        lines.push(line);
        line = word;
        return;
      }
      line = candidate;
    });
    lines.push(line);
    return lines;
  });

const textFitsLayer = (
  context: CanvasRenderingContext2D,
  layer: TemplateLayer,
  text: string,
  fontSize: number
) => {
  if (!layer.typography) return true;
  const { typography } = layer;
  context.font = getCanvasFont(typography, fontSize);
  const lines = getWrappedTextLines(
    context,
    text,
    layer.size.width,
    typography.letterSpacing
  );
  const widestLine = Math.max(
    ...lines.map((line) =>
      measureTextWidth(context, line, typography.letterSpacing)
    )
  );
  return (
    widestLine <= layer.size.width &&
    lines.length * fontSize * typography.lineHeight <= layer.size.height
  );
};

const updateTextToFitLayer = (
  layer: TemplateLayer,
  text: string,
  maximumFontSize = layer.typography?.fontSize
) => {
  if (
    layer.kind !== 'text' ||
    !layer.typography ||
    !maximumFontSize ||
    typeof document === 'undefined'
  )
    return { ...layer, text };

  const context = document.createElement('canvas').getContext('2d');
  if (!context) return { ...layer, text };

  const fontSize = Math.max(MINIMUM_TEXT_FONT_SIZE, maximumFontSize);
  if (textFitsLayer(context, layer, text, fontSize))
    return {
      ...layer,
      text,
      typography: { ...layer.typography, fontSize }
    };

  let lowerBound = MINIMUM_TEXT_FONT_SIZE;
  let upperBound = Math.floor(fontSize);
  let fittingFontSize = MINIMUM_TEXT_FONT_SIZE;
  while (lowerBound <= upperBound) {
    const candidate = Math.floor((lowerBound + upperBound) / 2);
    if (textFitsLayer(context, layer, text, candidate)) {
      fittingFontSize = candidate;
      lowerBound = candidate + 1;
    } else {
      upperBound = candidate - 1;
    }
  }

  return {
    ...layer,
    text,
    typography: { ...layer.typography, fontSize: fittingFontSize }
  };
};

const expandTextLayerToFit = (layer: TemplateLayer, text: string) => {
  if (layer.kind !== 'text' || !layer.typography)
    return { ...layer, text };

  const { typography } = layer;
  const lines = text.split('\n');
  const minimumHeight = Math.ceil(
    lines.length * typography.fontSize * typography.lineHeight +
      typography.fontSize * 0.35
  );
  return {
    ...layer,
    text,
    size: {
      ...layer.size,
      height: Math.max(layer.size.height, minimumHeight)
    }
  };
};

const QuickTextLayerField: React.FC<{
  layer: TemplateLayer;
  moreOptionsLabel: string;
  onUpdate: (id: string, updater: (layer: TemplateLayer) => TemplateLayer) => void;
  onFocusLayer: (id: string) => void;
  onMoreOptions: (id: string) => void;
}> = ({ layer, moreOptionsLabel, onUpdate, onFocusLayer, onMoreOptions }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const onUpdateRef = useRef(onUpdate);
  const maximumFontSizeRef = useRef(layer.typography?.fontSize);
  onUpdateRef.current = onUpdate;

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [layer.text]);

  return (
    <Form.Group>
      <Form.Label className="mb-1 small fw-semibold">{layer.name}</Form.Label>
      <Form.Control
        as="textarea"
        ref={textareaRef}
        rows={1}
        value={layer.text ?? ''}
        disabled={Boolean(layer.textLocked)}
        onFocus={() => onFocusLayer(layer.id)}
        onChange={(event) => {
          const text = event.target.value;
          onUpdateRef.current(layer.id, (current) =>
            updateTextToFitLayer(current, text, maximumFontSizeRef.current)
          );
        }}
      />
      <div className="mt-1 d-flex justify-content-end">
        <Button
          type="button"
          size="sm"
          variant="link"
          className="px-0 fw-normal text-muted"
          onClick={() => onMoreOptions(layer.id)}
        >
          {moreOptionsLabel}
        </Button>
      </div>
    </Form.Group>
  );
};

const QuickTextLayerFields: React.FC<{
  layers: TemplateLayer[];
  language: TemplateEditorLanguage;
  onUpdate: (id: string, updater: (layer: TemplateLayer) => TemplateLayer) => void;
  onFocusLayer: (id: string) => void;
  onMoreOptions: (id: string) => void;
}> = ({ layers, language, onUpdate, onFocusLayer, onMoreOptions }) => {
  const { t: translate } = useTranslate();
  const t = useCallback(
    (key: string, defaultValue: string) =>
      translate({ key, defaultValue, language }),
    [language, translate]
  );

  return (
    <div className="d-grid gap-3 border-bottom p-3">
      {layers.map((layer) => (
        <QuickTextLayerField
          key={layer.id}
          layer={layer}
          moreOptionsLabel={t(
            'templateEditor.actions.moreOptions',
            'More options'
          )}
          onUpdate={onUpdate}
          onFocusLayer={onFocusLayer}
          onMoreOptions={onMoreOptions}
        />
      ))}
    </div>
  );
};

const LayerFields: React.FC<{
  layer: TemplateLayer;
  language: TemplateEditorLanguage;
  fontOptions: TemplateEditorFontOption[];
  showTextLock: boolean;
  allowTextDetailsWhenLayerLocked: boolean;
  onFontSelect: (font: TemplateEditorFontOption) => void;
  onFontPickerFocus: () => void;
  onUpdate: (updater: (layer: TemplateLayer) => TemplateLayer) => void;
  onMove: (direction: -1 | 1) => void;
  onDelete: () => void;
  onImageSelect: (file: File) => void | Promise<void>;
  onImageRemove: () => void;
}> = ({
  layer,
  language,
  fontOptions,
  showTextLock,
  allowTextDetailsWhenLayerLocked,
  onFontSelect,
  onFontPickerFocus,
  onUpdate,
  onMove,
  onDelete,
  onImageSelect,
  onImageRemove
}) => {
  const { t: translate } = useTranslate();
  const t = useCallback(
    (key: string, defaultValue: string) =>
      translate({ key, defaultValue, language }),
    [language, translate]
  );
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const textDetailsLocked =
    layer.kind === 'text' &&
    (layer.textLocked || (!allowTextDetailsWhenLayerLocked && layer.locked));
  const pintura: Record<string, unknown> =
    layer.kind === 'text'
      ? { text: layer.text, ...layer.typography }
      : layer.kind === 'shape'
      ? {
          backgroundColor: layer.fillColor,
          cornerRadius: layer.cornerRadius,
          shapeType: layer.shapeType ?? 'rectangle'
        }
      : layer.kind === 'background'
      ? {
          backgroundColor: layer.fillColor ?? '#ffffff',
          backgroundImage: layer.image?.src,
          backgroundSize: layer.image?.fit ?? 'cover'
        }
      : {
          backgroundImage: layer.image?.src,
          backgroundSize:
            layer.image?.fit === 'stretch'
              ? 'stretch'
              : layer.image?.fit === 'natural'
              ? 'natural'
              : layer.image?.fit ?? 'contain',
          cornerRadius: layer.cornerRadius,
          opacity: layer.image?.opacity ?? 1,
          borderColor: layer.image?.borderColor ?? '#000000',
          borderWidth: layer.image?.borderWidth ?? 0
        };
  const setPintura = (property: string, fieldValue: unknown) =>
    onUpdate((current) => {
      if (current.kind === 'text') {
        if (property === 'text') {
          return expandTextLayerToFit(current, String(fieldValue));
        }
        const typography = { ...current.typography };
        Object.assign(typography, { [property]: fieldValue });
        return { ...current, typography };
      }
      if (current.kind === 'background') {
        if (property === 'backgroundColor')
          return { ...current, fillColor: String(fieldValue) };
        const fit =
          fieldValue === 'contain'
            ? 'contain'
            : fieldValue === 'stretch'
            ? 'stretch'
            : 'cover';
        return {
          ...current,
          image: { ...current.image, src: current.image?.src, fit }
        };
      }
      if (current.kind === 'shape')
        return property === 'backgroundColor'
          ? { ...current, fillColor: String(fieldValue) }
          : property === 'shapeType'
          ? {
              ...current,
              shapeType: fieldValue as TemplateLayer['shapeType'],
              ...(fieldValue === 'straightLine'
                ? {
                    size: {
                      ...current.size,
                      height: Math.min(current.size.height, 12)
                    }
                  }
                : {})
            }
          : { ...current, cornerRadius: Number(fieldValue) };
      if (property === 'cornerRadius') {
        return { ...current, cornerRadius: Math.max(0, Number(fieldValue)) };
      }
      if (property === 'opacity') {
        return {
          ...current,
          image: {
            ...current.image,
            fit: current.image?.fit ?? 'contain',
            opacity: Math.min(1, Math.max(0, Number(fieldValue)))
          }
        };
      }
      if (property === 'borderColor' || property === 'borderWidth') {
        return {
          ...current,
          image: {
            ...current.image,
            fit: current.image?.fit ?? 'contain',
            [property]:
              property === 'borderWidth'
                ? Math.max(0, Number(fieldValue))
                : String(fieldValue)
          }
        };
      }
      const fit =
        fieldValue === 'contain'
          ? 'contain'
          : fieldValue === 'stretch'
          ? 'stretch'
          : fieldValue === 'natural'
          ? 'natural'
          : 'cover';
      return {
        ...current,
        image: { ...current.image, src: current.image?.src, fit }
      };
    });
  const backgroundFillIsTransparent = layer.fillColor === 'transparent';
  const selectedFontFamily = String(pintura.fontFamily ?? 'Arial');
  const typeaheadFontOptions = fontOptions.some(
    (font) => font.value === selectedFontFamily
  )
    ? fontOptions
    : [
        { value: selectedFontFamily, label: selectedFontFamily },
        ...fontOptions
      ];
  return (
    <div>
      <div className="d-flex align-items-center justify-content-between gap-2 border-bottom p-3">
        <div className="min-w-0">
          <strong>{t('templateEditor.tabs.editLayer', 'Edit layer')}</strong>
          <small className="d-block text-muted">{layer.name}</small>
        </div>
        <div className="d-flex flex-shrink-0 align-items-center gap-1">
          {layer.kind !== 'background' && (
            <>
              <LomavisOverlayToolTip1
                placement="bottom"
                creatorStyle
                text={t(
                  'templateEditor.actions.sendBackHelp',
                  'Move this layer one position backward.'
                )}
              >
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="lomavis-template-layer-action bg-white text-gray-700"
                  aria-label={t(
                    'templateEditor.actions.sendBack',
                    'Send back'
                  )}
                  disabled={layer.locked}
                  onClick={() => onMove(-1)}
                >
                  <FontAwesomeIcon
                    icon={templateEditorIcons.sendBackward}
                    aria-hidden="true"
                  />
                </Button>
              </LomavisOverlayToolTip1>
              <LomavisOverlayToolTip1
                placement="bottom"
                creatorStyle
                text={t(
                  'templateEditor.actions.bringForwardHelp',
                  'Move this layer one position forward.'
                )}
              >
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="lomavis-template-layer-action bg-white text-gray-700"
                  aria-label={t(
                    'templateEditor.actions.bringForward',
                    'Bring forward'
                  )}
                  disabled={layer.locked}
                  onClick={() => onMove(1)}
                >
                  <FontAwesomeIcon
                    icon={templateEditorIcons.bringForward}
                    aria-hidden="true"
                  />
                </Button>
              </LomavisOverlayToolTip1>
            </>
          )}
          <LomavisOverlayToolTip1
            placement="bottom"
            creatorStyle
            text={t(
              'templateEditor.actions.deleteLayerHelp',
              'Permanently remove this layer from the overlay.'
            )}
          >
            <Button
              type="button"
              size="sm"
              variant="light"
              className="lomavis-template-layer-action bg-white text-danger"
              aria-label={t(
                'templateEditor.actions.deleteLayer',
                'Delete layer'
              )}
              disabled={layer.locked}
              onClick={() => setShowDeleteConfirmation(true)}
            >
              <FontAwesomeIcon
                icon={templateEditorIcons.trash}
                aria-hidden="true"
              />
            </Button>
          </LomavisOverlayToolTip1>
        </div>
      </div>
      <div className="lomavis-template-layer-fields d-grid gap-2 p-3">
        <Form.Group>
          <Form.Label>{t('templateEditor.layerName', 'Layer name')}</Form.Label>
          <div className="d-flex align-items-stretch gap-2">
            <Form.Control
              value={layer.name}
              disabled={layer.locked}
              onChange={(event) =>
                onUpdate((current) => ({
                  ...current,
                  name: event.target.value
                }))
              }
            />
            <div className="lomavis-template-layer-name-actions d-flex flex-shrink-0 align-items-stretch gap-1">
              <LomavisOverlayToolTip1
                placement="top"
                creatorStyle
                text={
                  layer.visible
                    ? t(
                        'templateEditor.actions.hideLayerHelp',
                        'Hide this layer from the overlay.'
                      )
                    : t(
                        'templateEditor.actions.showLayerHelp',
                        'Show this layer in the overlay.'
                      )
                }
              >
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="lomavis-template-layer-action lomavis-template-layer-name-action bg-white text-gray-700"
                  aria-label={t(
                    'templateEditor.actions.toggleVisibility',
                    'Toggle layer visibility'
                  )}
                  onClick={() =>
                    onUpdate((current) => ({
                      ...current,
                      visible: !current.visible
                    }))
                  }
                >
                  <FontAwesomeIcon
                    icon={
                      layer.visible
                        ? templateEditorIcons.eye
                        : templateEditorIcons.eyeSlash
                    }
                    aria-hidden="true"
                  />
                </Button>
              </LomavisOverlayToolTip1>
              <LomavisOverlayToolTip1
                placement="top"
                creatorStyle
                text={
                  layer.locked
                    ? t(
                        'templateEditor.actions.unlockLayerHelp',
                        'Unlock this layer to edit its properties.'
                      )
                    : t(
                        'templateEditor.actions.lockLayerHelp',
                        'Lock this layer to prevent edits.'
                      )
                }
              >
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="lomavis-template-layer-action lomavis-template-layer-name-action bg-white text-gray-700"
                  aria-label={t(
                    'templateEditor.actions.toggleLock',
                    'Toggle layer lock'
                  )}
                  onClick={() =>
                    onUpdate((current) => {
                      const locked = !current.locked;
                      return {
                        ...current,
                        locked,
                        ...(current.kind === 'text' && !locked
                          ? { textLocked: false }
                          : {})
                      };
                    })
                  }
                >
                  <FontAwesomeIcon
                    icon={
                      layer.locked
                        ? templateEditorIcons.lock
                        : templateEditorIcons.lockOpen
                    }
                    aria-hidden="true"
                  />
                </Button>
              </LomavisOverlayToolTip1>
            </div>
          </div>
        </Form.Group>
        {layer.kind === 'text' && (
          <>
            <div className="border-top pt-2 text-uppercase text-muted small fw-bold">
              {t('templateEditor.typography', 'Typography')}
            </div>
            <Form.Group>
              <Form.Label>
                {t('templateEditor.text.content', 'Text')}
              </Form.Label>
              <Form.Control
                as="textarea"
                value={String(pintura.text ?? '')}
                disabled={textDetailsLocked}
                onChange={(event) => setPintura('text', event.target.value)}
              />
            </Form.Group>
            {showTextLock && (
              <Form.Check
                type="switch"
                id={`template-editor-text-lock-${layer.id}`}
                label={t('templateEditor.text.lock', 'Lock text')}
                checked={layer.textLocked ?? false}
                onChange={(event) =>
                  onUpdate((current) => ({
                    ...current,
                    textLocked: event.target.checked,
                    locked: event.target.checked ? true : current.locked
                  }))
                }
              />
            )}
            <div
              className="d-grid gap-2"
              style={{ gridTemplateColumns: '1fr 1fr' }}
            >
              <Form.Group>
                <Form.Label>{t('templateEditor.text.font', 'Font')}</Form.Label>
                <LomavisFontTypeahead
                  id={`template-editor-font-${layer.id}`}
                  options={typeaheadFontOptions}
                  value={selectedFontFamily}
                  disabled={textDetailsLocked}
                  placeholder={t(
                    'templateEditor.text.searchFont',
                    'Search fonts…'
                  )}
                  onFocus={onFontPickerFocus}
                  onPreview={loadBunnyFont}
                  onSelect={(font) => {
                    onFontSelect(font);
                    setPintura('fontFamily', font.value);
                  }}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>{t('templateEditor.text.size', 'Size')}</Form.Label>
                <Form.Control
                  type="number"
                  value={Number(pintura.fontSize ?? 48)}
                  disabled={textDetailsLocked}
                  onChange={(event) =>
                    setPintura('fontSize', Number(event.target.value))
                  }
                />
              </Form.Group>
            </div>
            <Form.Group>
              <Form.Label>
                {t('templateEditor.text.formatting', 'Formatting')}
              </Form.Label>
              <div className="lomavis-template-text-toolbar" role="toolbar">
                <Button
                  type="button"
                  size="sm"
                  variant={
                    pintura.fontWeight === 'bold' ? 'primary' : 'light'
                  }
                  className="lomavis-template-text-toolbar-button"
                  aria-label={t('templateEditor.text.bold', 'Bold')}
                  aria-pressed={pintura.fontWeight === 'bold'}
                  disabled={textDetailsLocked}
                  onClick={() =>
                    setPintura(
                      'fontWeight',
                      pintura.fontWeight === 'bold' ? 'normal' : 'bold'
                    )
                  }
                >
                  <FontAwesomeIcon
                    icon={templateEditorIcons.bold}
                    aria-hidden="true"
                  />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={
                    pintura.fontStyle === 'italic' ? 'primary' : 'light'
                  }
                  className="lomavis-template-text-toolbar-button"
                  aria-label={t('templateEditor.text.italic', 'Italic')}
                  aria-pressed={pintura.fontStyle === 'italic'}
                  disabled={textDetailsLocked}
                  onClick={() =>
                    setPintura(
                      'fontStyle',
                      pintura.fontStyle === 'italic' ? 'normal' : 'italic'
                    )
                  }
                >
                  <FontAwesomeIcon
                    icon={templateEditorIcons.italic}
                    aria-hidden="true"
                  />
                </Button>
                <span className="lomavis-template-text-toolbar-divider" />
                {[
                  {
                    value: 'left',
                    label: t('templateEditor.text.left', 'Align left'),
                    icon: templateEditorIcons.alignLeft
                  },
                  {
                    value: 'center',
                    label: t('templateEditor.text.center', 'Align center'),
                    icon: templateEditorIcons.alignCenter
                  },
                  {
                    value: 'right',
                    label: t('templateEditor.text.right', 'Align right'),
                    icon: templateEditorIcons.alignRight
                  }
                ].map(({ value, label, icon: Icon }) => (
                  <Button
                    key={value}
                    type="button"
                    size="sm"
                    variant={
                      (pintura.textAlign ?? 'left') === value
                        ? 'primary'
                        : 'light'
                    }
                    className="lomavis-template-text-toolbar-button"
                    aria-label={label}
                    aria-pressed={(pintura.textAlign ?? 'left') === value}
                    disabled={textDetailsLocked}
                    onClick={() => setPintura('textAlign', value)}
                  >
                    <FontAwesomeIcon icon={Icon} aria-hidden="true" />
                  </Button>
                ))}
                <span className="lomavis-template-text-toolbar-divider" />
                <LomavisSketchColorPicker
                  compact
                  value={String(pintura.color ?? '#000000')}
                  disabled={textDetailsLocked}
                  ariaLabel={t('templateEditor.text.color', 'Text color')}
                  onChange={(value) => setPintura('color', value)}
                />
              </div>
            </Form.Group>
            <div
              className="d-grid gap-2"
              style={{ gridTemplateColumns: '1fr 1fr' }}
            >
              <Form.Group>
                <Form.Label>
                  {t('templateEditor.text.lineHeight', 'Line height')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0.5"
                  step="0.05"
                  value={Number(pintura.lineHeight ?? 1.1)}
                  disabled={textDetailsLocked}
                  onChange={(event) =>
                    setPintura('lineHeight', Number(event.target.value))
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>
                  {t('templateEditor.text.letterSpacing', 'Letter spacing')}
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  value={Number(pintura.letterSpacing ?? 0)}
                  disabled={textDetailsLocked}
                  onChange={(event) =>
                    setPintura('letterSpacing', Number(event.target.value))
                  }
                />
              </Form.Group>
            </div>
          </>
        )}
        {layer.kind === 'shape' && (
          <>
            <Form.Group>
              <Form.Label>{t('templateEditor.shape.type', 'Shape')}</Form.Label>
              <BootstrapDropdown
                value={String(pintura.shapeType ?? 'rectangle')}
                disabled={layer.locked}
                ariaLabel={t('templateEditor.shape.type', 'Shape')}
                onChange={(value) => setPintura('shapeType', value)}
                options={[
                  {
                    value: 'rectangle',
                    label: t('templateEditor.shape.rectangle', 'Rectangle')
                  },
                  {
                    value: 'ellipse',
                    label: t('templateEditor.shape.ellipse', 'Ellipse')
                  },
                  {
                    value: 'triangle',
                    label: t('templateEditor.shape.triangle', 'Triangle')
                  },
                  {
                    value: 'straightLine',
                    label: t(
                      'templateEditor.shape.straightLine',
                      'Straight line'
                    )
                  },
                  {
                    value: 'wavyLine',
                    label: t('templateEditor.shape.wavyLine', 'Wavy line')
                  },
                  {
                    value: 'arrow',
                    label: t('templateEditor.shape.arrow', 'Arrow')
                  },
                  {
                    value: 'diamond',
                    label: t('templateEditor.shape.diamond', 'Diamond')
                  },
                  {
                    value: 'star',
                    label: t('templateEditor.shape.star', 'Star')
                  }
                ]}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t('templateEditor.shape.fill', 'Fill color')}
              </Form.Label>
              <LomavisSketchColorPicker
                value={String(pintura.backgroundColor ?? '#3359c7')}
                disabled={layer.locked}
                ariaLabel={t('templateEditor.shape.fill', 'Fill color')}
                onChange={(value) => setPintura('backgroundColor', value)}
              />
            </Form.Group>
            {((layer.shapeType ?? 'rectangle') === 'rectangle' ||
              layer.shapeType === 'straightLine') && (
              <Form.Group>
                <Form.Label>
                  {t('templateEditor.shape.cornerRadius', 'Corner radius')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={Number(pintura.cornerRadius ?? 0)}
                  disabled={layer.locked}
                  onChange={(event) =>
                    setPintura('cornerRadius', Number(event.target.value))
                  }
                />
              </Form.Group>
            )}
          </>
        )}
        {layer.kind === 'background' && (
          <>
            <div className="text-muted small">
              {t(
                'templateEditor.background.hint',
                'The background stays behind every other layer. Select it on the canvas to move, resize, or rotate its image.'
              )}
            </div>
            <Form.Group>
              <Form.Label>
                {t('templateEditor.background.fill', 'Fill color')}
              </Form.Label>
              <LomavisSketchColorPicker
                value={
                  backgroundFillIsTransparent
                    ? '#ffffff'
                    : String(pintura.backgroundColor ?? '#ffffff')
                }
                disabled={layer.locked}
                ariaLabel={t(
                  'templateEditor.background.fill',
                  'Fill color'
                )}
                onChange={(value) => setPintura('backgroundColor', value)}
              />
            </Form.Group>
            {!backgroundFillIsTransparent && (
              <Button
                variant="outline-secondary"
                className="rounded-3"
                disabled={layer.locked}
                onClick={() => setPintura('backgroundColor', 'transparent')}
              >
                {t('templateEditor.background.clearFill', 'Clear fill')}
              </Button>
            )}
            <LomavisImageUpload1
              disabled={layer.locked}
              onFileSelect={onImageSelect}
              imageSrc={layer.image?.src}
              onImageRemove={onImageRemove}
              removeLabel={t('templateEditor.image.remove', 'Remove image')}
              acceptedFileTypes={['image/jpeg', 'image/png']}
              title={t('templateEditor.imageUpload.title', 'Select an image to upload')}
              description={t(
                'templateEditor.imageUpload.description',
                'or drag and drop a PNG or JPG image here'
              )}
              uploadingLabel={t(
                'templateEditor.imageUpload.uploadingLabel',
                'Uploading image…'
              )}
              uploadingDescription={t(
                'templateEditor.imageUpload.uploadingDescription',
                'Please wait while your image is uploaded.'
              )}
              invalidFileMessage={t(
                'templateEditor.imageUpload.invalidFile',
                'Choose a PNG or JPG image.'
              )}
            />
            {layer.image?.src && (
              <Form.Group>
                <Form.Label>
                  {t('templateEditor.imageFit', 'Image fit')}
                </Form.Label>
                <BootstrapDropdown
                  value={String(pintura.backgroundSize ?? 'cover')}
                  disabled={layer.locked}
                  ariaLabel={t('templateEditor.imageFit', 'Image fit')}
                  onChange={(value) => setPintura('backgroundSize', value)}
                  options={[
                    {
                      value: 'cover',
                      label: t('templateEditor.fit.cover', 'Cover')
                    },
                    {
                      value: 'contain',
                      label: t('templateEditor.fit.contain', 'Contain')
                    },
                    {
                      value: 'stretch',
                      label: t('templateEditor.fit.stretch', 'Stretch to fill')
                    }
                  ]}
                />
              </Form.Group>
            )}
          </>
        )}
        {layer.kind === 'image' && (
          <>
            <LomavisImageUpload1
              disabled={layer.locked}
              onFileSelect={onImageSelect}
              imageSrc={layer.image?.src}
              onImageRemove={onImageRemove}
              removeLabel={t('templateEditor.image.remove', 'Remove image')}
              acceptedFileTypes={['image/jpeg', 'image/png']}
              title={t('templateEditor.imageUpload.title', 'Select an image to upload')}
              description={t(
                'templateEditor.imageUpload.description',
                'or drag and drop a PNG or JPG image here'
              )}
              uploadingLabel={t(
                'templateEditor.imageUpload.uploadingLabel',
                'Uploading image…'
              )}
              uploadingDescription={t(
                'templateEditor.imageUpload.uploadingDescription',
                'Please wait while your image is uploaded.'
              )}
              invalidFileMessage={t(
                'templateEditor.imageUpload.invalidFile',
                'Choose a PNG or JPG image.'
              )}
            />
            <Form.Group>
              <Form.Label>
                {t('templateEditor.imageFit', 'Image fit')}
              </Form.Label>
              <BootstrapDropdown
                value={
                  layer.image?.fit === 'natural'
                    ? 'contain'
                    : layer.image?.fit ?? 'cover'
                }
                disabled={layer.locked}
                ariaLabel={t('templateEditor.imageFit', 'Image fit')}
                onChange={(value) => setPintura('backgroundSize', value)}
                options={[
                  {
                    value: 'contain',
                    label: t('templateEditor.fit.contain', 'Contain')
                  },
                  {
                    value: 'cover',
                    label: t('templateEditor.fit.cover', 'Cover')
                  },
                  {
                    value: 'stretch',
                    label: t('templateEditor.fit.stretch', 'Stretch to fill')
                  }
                ]}
              />
            </Form.Group>
            <div
              className="d-grid gap-2"
              style={{ gridTemplateColumns: '1fr 1fr' }}
            >
              <Form.Group>
                <Form.Label>
                  {t('templateEditor.image.cornerRadius', 'Corner radius')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={Number(pintura.cornerRadius ?? 0)}
                  disabled={layer.locked}
                  onChange={(event) =>
                    setPintura('cornerRadius', Number(event.target.value))
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>
                  {t('templateEditor.image.opacity', 'Opacity')}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    value={Math.round(Number(pintura.opacity ?? 1) * 100)}
                    disabled={layer.locked}
                    onChange={(event) =>
                      setPintura('opacity', Number(event.target.value) / 100)
                    }
                  />
                  <InputGroup.Text
                    className={layer.locked ? 'text-muted' : ''}
                  >
                    %
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </div>
            <div
              className="d-grid gap-2"
              style={{ gridTemplateColumns: '1fr 1fr' }}
            >
              <Form.Group>
                <Form.Label>
                  {t('templateEditor.image.borderColor', 'Border color')}
                </Form.Label>
                <LomavisSketchColorPicker
                  value={String(pintura.borderColor ?? '#000000')}
                  disabled={layer.locked}
                  ariaLabel={t(
                    'templateEditor.image.borderColor',
                    'Border color'
                  )}
                  onChange={(value) => setPintura('borderColor', value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>
                  {t('templateEditor.image.borderWidth', 'Border width')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={Number(pintura.borderWidth ?? 0)}
                  disabled={layer.locked}
                  onChange={(event) =>
                    setPintura('borderWidth', Number(event.target.value))
                  }
                />
              </Form.Group>
            </div>
          </>
        )}
      </div>
      <LomavisConfirmationModal
        show={showDeleteConfirmation}
        title={t('templateEditor.deleteLayer.title', 'Delete layer?')}
        description={t(
          'templateEditor.deleteLayer.description',
          'This layer will be permanently removed from the overlay.'
        )}
        cancelLabel={t('templateEditor.actions.cancel', 'Cancel')}
        confirmLabel={t('templateEditor.actions.delete', 'Delete')}
        buttonSize="sm"
        destructive
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={() => {
          setShowDeleteConfirmation(false);
          onDelete();
        }}
      />
    </div>
  );
};
